import React, { ReactNode, useEffect, useState } from 'react';
import { Accordion } from '@mantine/core';
import cn from 'classnames';
import { ArrowDownNew } from '@/components/Svg';
import styles from './customAccordion.module.scss';

export interface IProps {
  children: ReactNode;
  title: string;
  header?: ReactNode; // Optional custom header
  moreTitle?: string | ReactNode;
  onClick?: () => void;
  isAcc: boolean;
  isClickable?: boolean;
}

const CustomAccordion = ({
  children,
  onClick,
  title = '',
  header,
  isAcc = true,
}: IProps) => {
  const [open, setOpen] = useState<string | null>(!isAcc ? title : null);

  useEffect(() => {
    setOpen(!isAcc ? title : null);
  }, [isAcc, title]);

  const handleToggle = (value: string) => {
    if (isAcc || value === title) {
      setOpen(value === open ? null : value);
      if (onClick) onClick();
    }
  };

  const isOpen = open === title;

  return (
    <Accordion
      value={open}
      onChange={handleToggle}
      transitionDuration={300} // Match CSS transition duration
      className={styles.accordion}
      multiple={false} // Ensure only one panel is open at a time
    >
      <Accordion.Item
        className='relative bg-[#3C3B3B66]/[0.4] hover:bg-[#3C3B3B66]/[0.4] active:bg-[#3C3B3B66]/[0.4] active:border-0 border-0 rounded-[8px] p-0 m-0'
        value={title}
      >
        <Accordion.Control
          className={cn(
            'bg-[#3C3B3B66]/[0.4] hover:bg-[#3C3B3B66]/[0.4] active:bg-[#3C3B3B66]/[0.4] active:border-0 border-0 rounded-[8px] p-0 m-0 flex justify-between items-center transition-all duration-100',
            {
              'cursor-default': !isAcc,
              [styles.controlOpen]: isOpen,
              [styles.controlClosed]: !isOpen,
            },
          )}
        >
          {/* Header Content: Render only when accordion is closed */}
          {!isOpen && (
            <div className={cn(styles.headerContainer)}>
              {header ? header : <span className={styles.title}>{title}</span>}
              <div
                className={cn('transition-transform duration-100', {
                  'rotate-180': isOpen,
                })}
              >
                <ArrowDownNew color='white' />
              </div>
            </div>
          )}
        </Accordion.Control>

        <Accordion.Panel className='text-white m-0 relative'>
          <div className='max-h-[400px] overflow-auto'>
            <div className='flex-grow mt-2 mr-2'>{children}</div>
            <div
              className='z-10 absolute right-2 top-1 flex items-center h-10 cursor-pointer transition-transform duration-100 rotate-180 w-full'
              onClick={() => setOpen(null)}
            >
              <ArrowDownNew color='white' />
            </div>
          </div>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default CustomAccordion;
